import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus } from "lucide-react";
import { ICONS, ServiceIcon } from "@/components/ServiceIcon";

type Service = {
  id: string; title: string; description: string; price: string | null;
  icon: string; visible: boolean; sort_order: number;
};

export function ServicesTab() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    const { data } = await supabase.from("services").select("*").order("sort_order");
    setServices(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save(s: Service) {
    const { error } = await supabase.from("services").update({
      title: s.title, description: s.description, price: s.price,
      icon: s.icon, visible: s.visible, sort_order: s.sort_order,
    }).eq("id", s.id);
    if (error) toast.error(error.message); else toast.success("Saved");
  }
  async function remove(id: string) {
    if (!confirm("Remove this service?")) return;
    const { error } = await supabase.from("services").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Removed"); load(); }
  }
  async function toggle(s: Service) {
    const { error } = await supabase.from("services").update({ visible: !s.visible }).eq("id", s.id);
    if (error) toast.error(error.message); else load();
  }
  async function add() {
    const { error } = await supabase.from("services").insert({
      title: "New Service", description: "Describe this service...",
      icon: "Wrench", sort_order: services.length + 1,
    });
    if (error) toast.error(error.message); else { toast.success("Added"); load(); }
  }

  const update = (id: string, patch: Partial<Service>) =>
    setServices((ls) => ls.map((x) => x.id === id ? { ...x, ...patch } : x));

  if (loading) return <p className="text-muted-foreground">Loading...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl">Services</h2>
          <p className="text-sm text-muted-foreground">Add, edit, reorder, hide/show services.</p>
        </div>
        <Button onClick={add} className="bg-gradient-primary"><Plus className="mr-1 h-4 w-4" /> Add Service</Button>
      </div>
      <div className="grid gap-4">
        {services.map((s) => (
          <div key={s.id} className="rounded-xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-primary text-primary-foreground">
                <ServiceIcon name={s.icon} className="h-6 w-6" />
              </div>
              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input value={s.title} onChange={(e) => update(s.id, { title: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Price</Label>
                  <Input value={s.price || ""} onChange={(e) => update(s.id, { price: e.target.value })} placeholder="e.g. From ₹499" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Description</Label>
                  <Textarea rows={2} value={s.description} onChange={(e) => update(s.id, { description: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Icon</Label>
                  <select value={s.icon} onChange={(e) => update(s.id, { icon: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    {Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Sort order</Label>
                  <Input type="number" value={s.sort_order} onChange={(e) => update(s.id, { sort_order: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
              <div className="flex items-center gap-2 text-sm">
                <Switch checked={s.visible} onCheckedChange={() => toggle(s)} />
                <span className="text-muted-foreground">{s.visible ? "Visible on site" : "Hidden"}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => save(s)}>Save changes</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
